/**
 * Base API Client
 * Centralized HTTP client with error handling, token injection, and request cancellation
 */

import { HttpError, HttpResponse, RequestConfig, QueryParams } from './httpTypes';
import { withQuery } from './endpoints';

// Get base URL from environment
// React Scripts injects env vars at build time
declare const process: {
  env: {
    REACT_APP_API_BASE_URL?: string;
    [key: string]: string | undefined;
  };
};

// Runtime override – set by the widget mount function so embedded instances
// can point at a different API server without a rebuild.
let _runtimeBaseUrl: string | null = null;

/**
 * Override the API base URL at runtime.
 * Takes priority over the REACT_APP_API_BASE_URL env var.
 */
export function setApiBaseUrl(url: string): void {
  _runtimeBaseUrl = url.replace(/\/+$/, ''); // strip trailing slashes
}

const getBaseUrl = (): string => {
  if (_runtimeBaseUrl) return _runtimeBaseUrl;
  // @ts-ignore - process.env is injected by react-scripts
  return process.env.REACT_APP_API_BASE_URL || '/api/v1';
};

/**
 * Get authentication token (if available)
 * Can be extended to read from localStorage, cookies, or context
 */
function getAuthToken(): string | null {
  // Try to get token from storage first
  const storedToken = localStorage.getItem('authToken');
  if (storedToken) {
    return storedToken;
  }
  
  // TODO: Implement token retrieval from your auth system
  // Example: return getTokenFromContext() or getTokenFromCookies();
  return null;
}

/**
 * Token refresh handler
 * This should be implemented based on your auth system
 */
let tokenRefreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  // If already refreshing, return the existing promise
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  // Create refresh promise
  tokenRefreshPromise = (async () => {
    // TODO: Implement your token refresh logic
    // Example:
    // const response = await fetch('/api/auth/refresh', { method: 'POST' });
    // const { token } = await response.json();
    // setAuthToken(token);
    // return token;
    
    // Placeholder: no token refresh implemented yet
    tokenRefreshPromise = null;
    return null;
  })();

  return tokenRefreshPromise;
}

/**
 * Create default headers for requests
 */
function createHeaders(customHeaders?: HeadersInit): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  // Add auth token if available
  const token = getAuthToken();
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Create AbortController with timeout
 */
function createAbortController(timeout?: number): AbortController {
  const controller = new AbortController();
  
  if (timeout) {
    setTimeout(() => controller.abort(), timeout);
  }
  
  return controller;
}

/**
 * Check if body is FormData
 */
function isFormData(body: unknown): body is FormData {
  return body instanceof FormData;
}

/**
 * Normalize error response
 */
async function normalizeError(response: Response): Promise<HttpError> {
  let errorData: unknown;
  
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      errorData = await response.json();
    } else {
      errorData = await response.text();
    }
  } catch {
    errorData = undefined;
  }
  
  return HttpError.fromResponse(response, errorData);
}

/**
 * Make HTTP request with error handling and token refresh
 */
async function request<T = unknown>(
  endpoint: string,
  method: string,
  body?: unknown,
  config?: RequestConfig,
  retryCount = 0
): Promise<HttpResponse<T>> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;
  
  // Determine content type
  const headers = isFormData(body)
    ? { ...config?.headers } // Don't set Content-Type for FormData, browser will set it with boundary
    : createHeaders(config?.headers);
  
  // Create abort controller
  const abortController = config?.signal 
    ? { signal: config.signal } 
    : createAbortController(config?.timeout || 30000);
  
  const signal = abortController instanceof AbortController ? abortController.signal : abortController.signal;
  
  try {
    // eslint-disable-next-line no-restricted-syntax
    const response = await fetch(url, {
      method,
      headers,
      body: isFormData(body) ? body : body ? JSON.stringify(body) : undefined,
      signal,
    });

    // Handle 401 Unauthorized - try token refresh once
    if (response.status === 401 && retryCount === 0) {
      const newToken = await refreshToken();
      if (newToken) {
        // Retry request with new token
        const retryHeaders = isFormData(body)
          ? { ...config?.headers, Authorization: `Bearer ${newToken}` }
          : createHeaders({ ...config?.headers, Authorization: `Bearer ${newToken}` });
        
        // eslint-disable-next-line no-restricted-syntax
        const retryResponse = await fetch(url, {
          method,
          headers: retryHeaders,
          body: isFormData(body) ? body : body ? JSON.stringify(body) : undefined,
          signal,
        });

        if (!retryResponse.ok) {
          throw await normalizeError(retryResponse);
        }

        return await parseResponse<T>(retryResponse);
      }
    }

    if (!response.ok) {
      throw await normalizeError(response);
    }

    return await parseResponse<T>(response);
  } catch (error) {
    if (error instanceof HttpError) {
      throw error;
    }
    
    // Handle network errors, timeouts, etc.
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new HttpError(0, 'Request Timeout', 'Request timed out or was aborted');
      }
      throw new HttpError(0, 'Network Error', error.message);
    }
    
    throw HttpError.fromError(error);
  }
}

/**
 * Parse response based on content type
 */
async function parseResponse<T>(response: Response): Promise<HttpResponse<T>> {
  let data: T;
  const contentType = response.headers.get('content-type') || '';
  
  if (contentType.includes('application/json')) {
    data = await response.json();
  } else if (contentType.includes('application/octet-stream') || 
             contentType.includes('text/csv') ||
             contentType.includes('application/csv')) {
    // For blob responses (CSV, binary files, etc.)
    data = (await response.blob()) as unknown as T;
  } else {
    data = (await response.text()) as unknown as T;
  }

  return {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  };
}

/**
 * GET request
 */
export async function get<T = unknown>(
  endpoint: string,
  query?: QueryParams,
  config?: RequestConfig
): Promise<HttpResponse<T>> {
  const url = withQuery(endpoint, query);
  return request<T>(url, 'GET', undefined, config);
}

/**
 * POST request
 */
export async function post<TIn = unknown, TOut = unknown>(
  endpoint: string,
  body?: TIn,
  config?: RequestConfig
): Promise<HttpResponse<TOut>> {
  return request<TOut>(endpoint, 'POST', body, config);
}

/**
 * PUT request
 */
export async function put<TIn = unknown, TOut = unknown>(
  endpoint: string,
  body?: TIn,
  config?: RequestConfig
): Promise<HttpResponse<TOut>> {
  return request<TOut>(endpoint, 'PUT', body, config);
}

/**
 * PATCH request
 */
export async function patch<TIn = unknown, TOut = unknown>(
  endpoint: string,
  body?: TIn,
  config?: RequestConfig
): Promise<HttpResponse<TOut>> {
  return request<TOut>(endpoint, 'PATCH', body, config);
}

/**
 * DELETE request
 */
export async function del<T = unknown>(
  endpoint: string,
  config?: RequestConfig
): Promise<HttpResponse<T>> {
  return request<T>(endpoint, 'DELETE', undefined, config);
}

/**
 * Upload file (multipart/form-data)
 */
export async function upload<T = unknown>(
  endpoint: string,
  formData: FormData,
  config?: RequestConfig
): Promise<HttpResponse<T>> {
  return request<T>(endpoint, 'POST', formData, {
    ...config,
    headers: {
      // Don't set Content-Type for FormData
      ...config?.headers,
    },
  });
}

/**
 * Download file as blob
 */
export async function download(
  endpoint: string,
  query?: QueryParams,
  config?: RequestConfig
): Promise<Blob> {
  const url = withQuery(endpoint, query);
  const response = await request<Blob>(url, 'GET', undefined, {
    ...config,
  });
  return response.data;
}

// Re-export auth functions for convenience
export { setAuthToken, getAuthToken as getAuthTokenFromStorage, clearAuth, isAuthenticated } from './auth';

